/**
 * Visualiseur de document PDF avec filigrane CONSULTATION
 * Design premium FTPE Outremer (Or et Vert)
 */
import React, { useState } from 'react';
import { ExternalLink, FileText, Maximize2 } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

export default function DocumentViewerFTPE({ 
  url, 
  title, 
  description,
  icon: Icon = FileText,
  color = "amber", // amber, emerald, slate
  onClose,
  isOpen,
  showWatermark = true
}) {
  const colorClasses = {
    amber: {
      bg: "from-amber-50 to-amber-100",
      border: "border-amber-300",
      text: "text-amber-900",
      icon: "from-amber-500 to-amber-600"
    },
    emerald: {
      bg: "from-emerald-50 to-emerald-100",
      border: "border-emerald-300",
      text: "text-emerald-900",
      icon: "from-emerald-500 to-emerald-600"
    },
    slate: {
      bg: "from-slate-50 to-slate-100",
      border: "border-slate-300",
      text: "text-slate-900",
      icon: "from-slate-500 to-slate-600"
    }
  };

  const colors = colorClasses[color] || colorClasses.amber;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[90vh] p-0 overflow-hidden">
        <DialogHeader className={`p-4 bg-gradient-to-r ${colors.bg} border-b ${colors.border}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 bg-gradient-to-br ${colors.icon} rounded-lg flex items-center justify-center shadow-lg`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <DialogTitle className={colors.text}>{title}</DialogTitle>
                {description && <p className="text-sm text-slate-600">{description}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a 
                href={url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Ouvrir
              </a>
            </div>
          </div>
        </DialogHeader>

        {/* Contenu avec filigrane */}
        <div className="relative flex-1 h-full bg-slate-100">
          {/* Filigrane CONSULTATION */}
          {showWatermark && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 overflow-hidden">
              <div 
                className="text-slate-300 text-6xl font-black tracking-widest opacity-30 select-none"
                style={{
                  transform: 'rotate(-35deg)',
                  whiteSpace: 'nowrap',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                CONSULTATION • CONSULTATION • CONSULTATION
              </div>
            </div>
          )}
          
          {/* Iframe PDF */}
          <iframe
            src={`${url}#toolbar=0&navpanes=0`}
            className="w-full h-full border-0"
            title={title}
            style={{ minHeight: 'calc(90vh - 80px)' }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Composant carte document avec aperçu
export function DocumentCardFTPE({
  url,
  title,
  subtitle,
  description,
  icon: Icon = FileText,
  color = "amber",
  badge,
  showWatermark = true
}) {
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const colorClasses = {
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-50 hover:border-amber-400",
      corner: "from-amber-400/20",
      icon: "from-amber-500 to-amber-600",
      title: "text-amber-900",
      subtitle: "text-amber-600",
      button: "from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-emerald-50 hover:border-emerald-400",
      corner: "from-emerald-400/20",
      icon: "from-emerald-500 to-emerald-600",
      title: "text-emerald-900",
      subtitle: "text-emerald-600",
      button: "from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700"
    },
    purple: {
      card: "border-purple-200 bg-gradient-to-br from-purple-50 via-white to-purple-50 hover:border-purple-400",
      corner: "from-purple-400/20",
      icon: "from-purple-500 to-purple-600",
      title: "text-purple-900",
      subtitle: "text-purple-600",
      button: "from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700"
    },
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 via-white to-slate-50 hover:border-amber-400",
      corner: "from-slate-300/20",
      icon: "from-slate-600 to-slate-700",
      title: "text-slate-900",
      subtitle: "text-slate-500",
      button: "from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800"
    }
  };

  const colors = colorClasses[color] || colorClasses.amber;

  return (
    <>
      <div className={`group relative overflow-hidden rounded-xl border-2 ${colors.card} hover:shadow-lg transition-all duration-300`}>
        <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl ${colors.corner} to-transparent`} />
        
        {badge && (
          <div className="absolute top-2 right-2">
            <span className="px-2 py-1 bg-amber-500 text-white text-[10px] font-bold rounded">
              {badge}
            </span>
          </div>
        )}

        <div className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-12 h-12 bg-gradient-to-br ${colors.icon} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className={`font-bold ${colors.title}`}>{title}</p>
              <p className={`text-xs ${colors.subtitle}`}>{subtitle}</p>
            </div>
          </div>
          
          <p className="text-xs text-slate-600 mb-4 line-clamp-2">
            {description}
          </p>
          
          <button 
            onClick={() => setIsViewerOpen(true)}
            className={`flex items-center justify-center gap-2 w-full py-2.5 bg-gradient-to-r ${colors.button} text-white rounded-lg font-semibold text-sm transition-all shadow-md hover:shadow-lg`}
          >
            <Maximize2 className="w-4 h-4" />
            Consulter
          </button>
        </div>
      </div>

      <DocumentViewerFTPE
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        url={url}
        title={title}
        description={subtitle}
        icon={Icon}
        color={color}
        showWatermark={showWatermark}
      />
    </>
  );
}
